import { Tldraw } from "tldraw";
import "tldraw/tldraw.css";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import logger from './../../../utils/logger.js';


export default function ExcalidrawModal({ onClose, isMobile, id, interviewId, question, sendSkipRequest }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');
  const [toastOpen, setToastOpen] = useState(false);
  const [issubmited, setIsSubmitted] = useState(false);
  const editorRef = useRef(null);
  const prevId = useRef(id);
  const prevQuestion = useRef(question);

  // Track the key to force a remount of the Tldraw component when question changes
  const [editorKey, setEditorKey] = useState(0);

  const prevIdRef = useRef(id);

  useEffect(() => {

    // Check if the question or id has changed
    const questionText = typeof question === 'string' ? question : question?.text;
    const prevQuestionText = typeof prevQuestion.current === 'string' ? prevQuestion.current : prevQuestion.current?.text;
    
    const hasQuestionChanged = questionText !== prevQuestionText || id !== prevId.current;
    
    if (hasQuestionChanged) {
      logger.debug("Question changed, clearing whiteboard");
      
      // Try multiple approaches to clear the board
      if (editorRef?.current) {
        // Approach 1: Try using editor methods
        if (typeof editorRef.current.deleteAll === 'function') {
          editorRef.current.deleteAll();
        } else if (typeof editorRef.current.reset === 'function') {
          editorRef.current.reset();
        } else if (typeof editorRef.current.clearCanvas === 'function') {
          editorRef.current.clearCanvas();
        }
        
        // Approach 2: Force a remount of the component
        setEditorKey(prev => prev + 1);
      }
      
      setIsSubmitted(false);
      prevId.current = id;
      prevQuestion.current = question;
    }
  }, [question, id]);


  const submitDiagram = async () => {
    try {
      if (!editorRef?.current) return;

      setIsSubmitting(true);
      const editor = editorRef?.current;
      const shapeIds = editor?.getCurrentPageShapeIds();
      if (shapeIds?.size === 0) {
        toast.error("Please draw something before submitting");
        setIsSubmitting(false);
        return;
      }

      // ✅ Normalize question (object with id & text)
      const questionData = typeof question === 'string'
        ? { text: question, id: "" }
        : question;

      if (!questionData?.text || !interviewId) {
        toast.error("Missing question or interview ID");
        logger.warn("Missing info:", { interviewId, questionData });
        setIsSubmitting(false);
        return;
      }

      const { blob } = await editor?.toImage([...shapeIds], {
        format: "png",
        background: true,
      });


      const formData = new FormData();
      formData.append(
        "request_data",
        JSON.stringify({
          interview_id: interviewId,
          question_id: id || "",
          question: questionData.text || "",
          
        })
      );
      formData.append("answer_image", blob, "diagram.png");

      const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
      const protocol = BASE_URL.startsWith("localhost") ? "http" : "https";

     

      const response = await fetch(`${protocol}://${BASE_URL}/api/hld-question`, {
        method: "POST",
       // credentials: "include",
        body: formData,
      });

      if (response.ok) {
        toast.success("Diagram submitted successfully");
        setIsSubmitted(true);
        // onClose();
      } else {
        const errorData = await response.json();
        toast.error(`Submission failed: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      logger.error("Error submitting diagram:", error);
      toast.error("Failed to submit diagram. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    sendSkipRequest();
    // setToastMessage('You have skipped the diagram question.');
    // setToastSeverity('info');
    setToastOpen(true);
    if (issubmited) {
      onClose();
      return;
    }
  };

  const handleMount = (editor) => {
    editorRef.current = editor;
  };

  return (
    <div
      className="tldraw__editor py-4"
      style={{
        height: isMobile ? "80vh" : "80vh",
        width: isMobile ? "90vw" : "67vw",
        marginTop: isMobile ? 40 : 135,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "12px",
        backgroundColor: "#f8fafc",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
      }}
    >
      {/* Drawing canvas - with key to force remount */}
      <div style={{ flex: 1, position: "relative" }}>
        <Tldraw key={editorKey} onMount={handleMount} />
      </div>
  
      {/* Action buttons */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          right: "5px",
          display: "flex",
          gap: "14px",
          zIndex: 9999,
          padding: "10px 20px",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
          alignItems: "center",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* ✅ Skip is always enabled */}
        <button
          onClick={handleSkip}
          disabled={false} 
          style={{
            fontSize: 14,
            backgroundColor: "#e2e8f0",
            color: "#1a1a1a",
            padding: "8px 20px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            cursor: "pointer",
            fontWeight: "600",
            transition: "all 0.2s ease-in-out",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#cbd5e1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#e2e8f0";
          }}
        >
          Skip
        </button>

        {/* ✅ Submit gets disabled during submission */}
        <button
          onClick={submitDiagram}
          disabled={isSubmitting || issubmited}
          style={{
            fontSize: 14,
            backgroundColor: (isSubmitting || issubmited) ? "#93c5fd" : "#1d58d8",
            color: "white",
            padding: "8px 20px",
            borderRadius: "6px",
            border: "none",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            fontWeight: "600",
            opacity: isSubmitting ? 0.6 : 1,
            transition: "all 0.2s ease-in-out",
            boxShadow: "0 2px 8px rgba(29, 88, 216, 0.3)",
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) e.currentTarget.style.backgroundColor = "#174bb9";
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) e.currentTarget.style.backgroundColor = "#1d58d8";
          }}
        >
          {isSubmitting ? "Submitting..." :  issubmited ? "Submitted": "Submit"}
        </button>
      </div>
  
      {/* Toast notification */}
      {/* {toastOpen && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg flex items-start gap-3 w-[300px] backdrop-blur bg-white/10 text-white 
          ${
            toastSeverity === "info"
              ? "border border-blue-300"
              : "border border-green-300"
          }`}
        >
          <div className="flex-1 text-sm font-medium">{toastMessage}</div>
          <button
            onClick={() => setToastOpen(false)}
            className="text-xl font-bold leading-none hover:opacity-70"
          >
            ×
          </button>
        </div>
      )} */}
    </div>
  );
}